import type { ConsentChannel, CrmSegment, CustomerKind, SegmentCriteria } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CheckboxField,
  Container,
  Dialog,
  DialogClose,
  DialogContent,
  Drawer,
  DrawerContent,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  NativeSelect,
  NoPermissionState,
  PageHeader,
  SettingSwitch,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Plus, Trash2, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/features/auth/session';
import { CONSENT_CHANNEL_ORDER } from '@/features/crm/labels';
import {
  createSegment,
  crmKeys,
  deleteSegment,
  previewSegment,
  segmentsQueryOptions,
  updateSegment,
} from '@/features/crm/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';

export const Route = createFileRoute('/_app/crm/segments')({ component: SegmentsPage });

/** Segments déclaratifs (ADR-025, D-H65). */
function SegmentsPage() {
  const { t } = useTranslation(['crm', 'common', 'errors']);
  const canRead = useCan('crm.read');
  const canManage = useCan('crm.manage');
  const queryClient = useQueryClient();
  const query = useQuery(segmentsQueryOptions(canRead));
  const [editing, setEditing] = useState<CrmSegment | null | 'new'>(null);
  const [deleting, setDeleting] = useState<CrmSegment | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => deleteSegment(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: crmKeys.all });
      toast.success(t('crm:segments.deleted'));
      setDeleting(null);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <PageHeader title={t('crm:segments.title')} />
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionBody')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('crm:segments.title')}
        subtitle={t('crm:segments.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('crm:title'), link: <Link to="/crm">{t('crm:title')}</Link> },
          { label: t('crm:segments.title') },
        ]}
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setEditing('new')}>
              <Plus className="ico" aria-hidden="true" />
              {t('crm:segments.new')}
            </Button>
          ) : undefined
        }
      />
      <Card>
        <CardBody density="flush">
          {query.isError ? (
            <ErrorState
              title={t('errors:generic.title')}
              description={errorMessage(query.error, t)}
              traceId={errorTraceId(query.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void query.refetch()}
              traceCopyLabel={t('common:trace.copy')}
              traceCopiedLabel={t('common:trace.copied')}
            />
          ) : query.isPending ? (
            <TableSkeleton rows={4} columns={4} label={t('common:states.loading')} />
          ) : query.data.length === 0 ? (
            <EmptyState
              icon={<Users className="ico ico-24" aria-hidden="true" />}
              title={t('crm:segments.empty')}
            />
          ) : (
            <TableWrap>
              <Table>
                <caption className="sr-only">{t('crm:segments.title')}</caption>
                <thead>
                  <tr>
                    <Th>{t('crm:segments.name')}</Th>
                    <Th align="right">{t('crm:segments.members')}</Th>
                    <Th>{t('crm:segments.active')}</Th>
                    <Th>{t('common:table.actions')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.map((segment) => (
                    <Tr key={segment.id}>
                      <Td>
                        <div className="strong">{segment.name}</div>
                        {segment.description ? (
                          <div className="muted t-12">{segment.description}</div>
                        ) : null}
                      </Td>
                      <Td align="right" numeric>
                        {t('crm:segments.membersCount', { count: segment.memberCount })}
                      </Td>
                      <Td>
                        <Badge tone={segment.isActive ? 'green' : 'neutral'}>
                          {segment.isActive ? t('common:actions.yes') : t('common:actions.no')}
                        </Badge>
                      </Td>
                      <Td>
                        <div className="row row--wrap" style={{ gap: 4 }}>
                          <Button variant="ghost" size="sm" asChild>
                            <Link to="/crm/customers" search={{ segmentId: segment.id }}>
                              {t('crm:segments.openList')}
                            </Link>
                          </Button>
                          <Button variant="ghost" size="sm" asChild>
                            <Link
                              to="/crm/follow-ups"
                              search={{ kind: 'SEGMENT', segmentId: segment.id }}
                            >
                              {t('crm:segments.followUp')}
                            </Link>
                          </Button>
                          {canManage ? (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => setEditing(segment)}>
                                {t('common:actions.edit')}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleting(segment)}
                              >
                                <Trash2 className="ico ico-14" aria-hidden="true" />
                                {t('crm:segments.delete')}
                              </Button>
                            </>
                          ) : null}
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </CardBody>
      </Card>

      {editing ? (
        <SegmentDrawer
          segment={editing === 'new' ? null : editing}
          open
          onOpenChange={(open) => !open && setEditing(null)}
        />
      ) : null}

      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent
          title={t('crm:segments.deleteTitle')}
          description={t('crm:segments.deleteBody')}
          icon={<Trash2 className="ico" aria-hidden="true" />}
          iconTone="red"
          closeLabel={t('common:actions.close')}
          footer={
            <>
              <DialogClose asChild>
                <Button variant="secondary">{t('common:actions.cancel')}</Button>
              </DialogClose>
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={() => deleting && remove.mutate(deleting.id)}
              >
                {t('crm:segments.delete')}
              </Button>
            </>
          }
        >
          <p className="t-13 mb-0">{deleting?.name}</p>
        </DialogContent>
      </Dialog>
    </Container>
  );
}

interface CriteriaForm {
  kinds: CustomerKind[];
  tags: string;
  tierCodes: string;
  minVisits: string;
  minSpend: string;
  periodMonths: string;
  lastVisitBefore: string;
  lastVisitAfter: string;
  inactiveDays: string;
  birthdayMonths: string;
  consentChannel: ConsentChannel | '';
  consentGranted: boolean;
  modules: ('hotel' | 'pos')[];
}

function formOf(criteria: SegmentCriteria | null): CriteriaForm {
  return {
    kinds: criteria?.kinds ?? [],
    tags: criteria?.tags?.join(', ') ?? '',
    tierCodes: criteria?.tierCodes?.join(', ') ?? '',
    minVisits: criteria?.minVisits?.toString() ?? '',
    minSpend: criteria?.minSpend?.toString() ?? '',
    periodMonths: String(criteria?.periodMonths ?? 12),
    lastVisitBefore: criteria?.lastVisitBefore ?? '',
    lastVisitAfter: criteria?.lastVisitAfter ?? '',
    inactiveDays: criteria?.inactiveDays?.toString() ?? '',
    birthdayMonths: criteria?.birthdayMonths?.join(', ') ?? '',
    consentChannel: criteria?.consent?.channel ?? '',
    consentGranted: criteria?.consent?.granted ?? true,
    modules: criteria?.modules ?? [],
  };
}

function list(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function criteriaOf(form: CriteriaForm): SegmentCriteria {
  const numberOr = (value: string) => (value.trim() ? Number(value) : undefined);
  const months = list(form.birthdayMonths)
    .map(Number)
    .filter((month) => Number.isInteger(month) && month >= 1 && month <= 12);
  return {
    ...(form.kinds.length > 0 ? { kinds: form.kinds } : {}),
    ...(list(form.tags).length > 0 ? { tags: list(form.tags) } : {}),
    ...(list(form.tierCodes).length > 0 ? { tierCodes: list(form.tierCodes) } : {}),
    ...(numberOr(form.minVisits) !== undefined ? { minVisits: numberOr(form.minVisits) } : {}),
    ...(numberOr(form.minSpend) !== undefined ? { minSpend: numberOr(form.minSpend) } : {}),
    periodMonths: numberOr(form.periodMonths) ?? 12,
    ...(form.lastVisitBefore ? { lastVisitBefore: form.lastVisitBefore } : {}),
    ...(form.lastVisitAfter ? { lastVisitAfter: form.lastVisitAfter } : {}),
    ...(numberOr(form.inactiveDays) !== undefined
      ? { inactiveDays: numberOr(form.inactiveDays) }
      : {}),
    ...(months.length > 0 ? { birthdayMonths: months } : {}),
    ...(form.consentChannel
      ? { consent: { channel: form.consentChannel, granted: form.consentGranted } }
      : {}),
    ...(form.modules.length > 0 ? { modules: form.modules } : {}),
  };
}

function SegmentDrawer({
  segment,
  open,
  onOpenChange,
}: {
  segment: CrmSegment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation(['crm', 'customers', 'common']);
  const queryClient = useQueryClient();
  const [name, setName] = useState(segment?.name ?? '');
  const [description, setDescription] = useState(segment?.description ?? '');
  const [isActive, setIsActive] = useState(segment?.isActive ?? true);
  const [form, setForm] = useState<CriteriaForm>(formOf(segment?.criteria ?? null));
  const [preview, setPreview] = useState<number | null>(segment?.memberCount ?? null);
  const set = <K extends keyof CriteriaForm>(key: K, value: CriteriaForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    const timer = window.setTimeout(() => {
      previewSegment(criteriaOf(form))
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 400);
    return () => window.clearTimeout(timer);
  }, [form]);

  const mutation = useMutation({
    mutationFn: () =>
      segment
        ? updateSegment(segment.id, {
            name: name.trim(),
            description: description.trim() || null,
            criteria: criteriaOf(form),
            isActive,
            version: segment.version,
          })
        : createSegment({
            name: name.trim(),
            description: description.trim() || null,
            criteria: criteriaOf(form),
            isActive,
          }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: crmKeys.all });
      toast.success(t('crm:segments.saved'));
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const toggle = <T extends string>(values: T[], value: T): T[] =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        title={segment ? t('crm:segments.edit') : t('crm:segments.new')}
        description={preview === null ? undefined : t('crm:segments.preview', { count: preview })}
        closeLabel={t('common:actions.close')}
        wide
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) mutation.mutate();
        }}
        footer={
          <>
            <Button variant="secondary" type="button" onClick={() => onOpenChange(false)}>
              {t('common:actions.cancel')}
            </Button>
            <Button variant="primary" type="submit" disabled={!name.trim() || mutation.isPending}>
              {t('crm:segments.save')}
            </Button>
          </>
        }
      >
        <div className="stack">
          <FormField label={t('crm:segments.name')} required>
            {(field) => (
              <Input
                id={field.id}
                value={name}
                maxLength={120}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </FormField>
          <FormField label={t('crm:segments.description')}>
            {(field) => (
              <Input
                id={field.id}
                value={description}
                maxLength={500}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </FormField>
          <SettingSwitch
            label={t('crm:segments.active')}
            checked={isActive}
            onCheckedChange={(value) => setIsActive(Boolean(value))}
          />
          <h3 className="t-13 strong mb-0">{t('crm:segments.criteria.title')}</h3>
          <div className="row row--wrap" style={{ gap: 12 }}>
            <span className="t-13">{t('crm:segments.criteria.kinds')}</span>
            {(['PERSON', 'COMPANY'] as CustomerKind[]).map((kind) => (
              <CheckboxField
                key={kind}
                label={t(`customers:kind.${kind}`)}
                checked={form.kinds.includes(kind)}
                onCheckedChange={() => set('kinds', toggle(form.kinds, kind))}
              />
            ))}
          </div>
          <div className="row row--wrap" style={{ gap: 12 }}>
            <span className="t-13">{t('crm:segments.criteria.modules')}</span>
            {(['hotel', 'pos'] as const).map((module) => (
              <CheckboxField
                key={module}
                label={t(`crm:segments.criteria.${module}`)}
                checked={form.modules.includes(module)}
                onCheckedChange={() => set('modules', toggle(form.modules, module))}
              />
            ))}
          </div>
          <div className="grid grid-2">
            <FormField label={t('crm:segments.criteria.tags')}>
              {(field) => (
                <Input
                  id={field.id}
                  value={form.tags}
                  onChange={(e) => set('tags', e.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('crm:segments.criteria.tierCodes')}>
              {(field) => (
                <Input
                  id={field.id}
                  value={form.tierCodes}
                  onChange={(e) => set('tierCodes', e.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('crm:segments.criteria.minVisits')}>
              {(field) => (
                <Input
                  id={field.id}
                  inputMode="numeric"
                  value={form.minVisits}
                  onChange={(e) => set('minVisits', e.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('crm:segments.criteria.minSpend')}>
              {(field) => (
                <Input
                  id={field.id}
                  inputMode="decimal"
                  value={form.minSpend}
                  onChange={(e) => set('minSpend', e.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('crm:segments.criteria.periodMonths')}>
              {(field) => (
                <Input
                  id={field.id}
                  inputMode="numeric"
                  value={form.periodMonths}
                  onChange={(e) => set('periodMonths', e.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('crm:segments.criteria.inactiveDays')}>
              {(field) => (
                <Input
                  id={field.id}
                  inputMode="numeric"
                  value={form.inactiveDays}
                  onChange={(e) => set('inactiveDays', e.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('crm:segments.criteria.lastVisitAfter')}>
              {(field) => (
                <Input
                  id={field.id}
                  type="date"
                  value={form.lastVisitAfter}
                  onChange={(e) => set('lastVisitAfter', e.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('crm:segments.criteria.lastVisitBefore')}>
              {(field) => (
                <Input
                  id={field.id}
                  type="date"
                  value={form.lastVisitBefore}
                  onChange={(e) => set('lastVisitBefore', e.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('crm:segments.criteria.birthdayMonths')}>
              {(field) => (
                <Input
                  id={field.id}
                  value={form.birthdayMonths}
                  onChange={(e) => set('birthdayMonths', e.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('crm:segments.criteria.consent')}>
              {(field) => (
                <NativeSelect
                  id={field.id}
                  value={
                    form.consentChannel
                      ? `${form.consentChannel}:${form.consentGranted ? '1' : '0'}`
                      : ''
                  }
                  onChange={(e) => {
                    const [channel, granted] = e.target.value.split(':');
                    set('consentChannel', (channel ?? '') as ConsentChannel | '');
                    set('consentGranted', granted !== '0');
                  }}
                >
                  <option value="">{t('crm:segments.criteria.consentAny')}</option>
                  {CONSENT_CHANNEL_ORDER.flatMap((channel) => [
                    <option key={`${channel}:1`} value={`${channel}:1`}>
                      {t('crm:segments.criteria.consentGranted', {
                        channel: t(`crm:consents.channels.${channel}`),
                      })}
                    </option>,
                    <option key={`${channel}:0`} value={`${channel}:0`}>
                      {t('crm:segments.criteria.consentDenied', {
                        channel: t(`crm:consents.channels.${channel}`),
                      })}
                    </option>,
                  ])}
                </NativeSelect>
              )}
            </FormField>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
